import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDashboard } from '../../context/DashboardContext';
import { usePortfolioView } from '../../context/PortfolioViewContext';
import { selectTopMovers } from '../../state/dashboardSelectors';
import type { TimeWindow } from '../../services/dashboardApi';
import { getTickerMarketBatch, getTickerMarket } from '../../services/tickerApi';
import { useAuth } from '../../../auth/hooks/useAuth';
import { ShowRealizedGainsToggle } from './ShowRealizedGainsToggle';

interface TopMoversCardProps {
  selectedTicker: string | null;
  onSelectTicker: (symbol: string) => void;
  hideShowRealizedGains?: boolean;
  className?: string;
}

/**
 * TopMoversCard displays a horizontal scrollable list of top movers for the day.
 * Allows users to click a mover to select it and view its chart.
 * 
 * Fetches ticker market data (company_name, day_pct_change) from lazy endpoint.
 */
export const TopMoversCard: React.FC<TopMoversCardProps> = ({
  selectedTicker,
  onSelectTicker,
  hideShowRealizedGains = false,
  className = '',
}) => {
  const { data, isLoading } = useDashboard();
  const { selectedTimeWindow, showRealizedGains } = usePortfolioView();
  const { session } = useAuth();
  
  // Get top mover ticker symbols from cache
  // Structure: topMovers[window] = {tickers: [...]}
  const topMoverSymbols = useMemo(() => {
    if (!data?.topMovers) return [];
    const topMovers = data.topMovers;
    const windowData = topMovers[selectedTimeWindow.key as TimeWindow];
    const tickerSymbols = windowData?.tickers;
    return Array.isArray(tickerSymbols) ? tickerSymbols : [];
  }, [data, selectedTimeWindow.key]);
  
  // Batch fetch ticker market data for top movers
  const { data: marketData, isLoading: isLoadingMarket } = useQuery({
    queryKey: ['ticker-market-batch', topMoverSymbols],
    queryFn: async () => {
      if (!session?.access_token || topMoverSymbols.length === 0) {
        return null;
      }
      return getTickerMarketBatch({
        tickers: topMoverSymbols,
        token: session.access_token,
      });
    },
    enabled: topMoverSymbols.length > 0 && !!session?.access_token,
    staleTime: 5 * 60 * 1000, // 5 minutes (matches backend TTL)
  });
  
  // Merge market data into dashboard data for selector
  const enrichedData = useMemo(() => {
    if (!data || !marketData?.data) return data;
    
    // Check if it's a batch response (has tickers property)
    const batchData = marketData.data as any;
    if (!batchData.tickers || typeof batchData.tickers !== 'object') return data;
    
    // Add ticker market data to topMovers.tickers structure
    const enriched = { ...data };
    if (!enriched.topMovers) {
      enriched.topMovers = {};
    }
    if (!enriched.topMovers.tickers) {
      enriched.topMovers.tickers = {};
    }
    
    // Merge market data into topMovers.tickers
    Object.entries(batchData.tickers).forEach(([ticker, marketInfo]: [string, any]) => {
      enriched.topMovers.tickers[ticker.toUpperCase()] = {
        company_name: marketInfo.company_name,
        dayPctChange: marketInfo.day_pct_change,
      };
    });
    
    return enriched;
  }, [data, marketData]);
  
  const topMovers = useMemo(
    () => selectTopMovers(enrichedData, selectedTimeWindow.key as TimeWindow),
    [enrichedData, selectedTimeWindow.key]
  );

  const unrealizedTickerSymbols = useMemo(() => {
    const tickerAccounts = data?.tickers;
    if (!tickerAccounts || typeof tickerAccounts !== 'object') return new Set<string>();

    const symbols = new Set<string>();
    Object.values(tickerAccounts).forEach((accountTickers) => {
      if (!accountTickers || typeof accountTickers !== 'object') return;
      Object.keys(accountTickers).forEach((symbol) => {
        symbols.add(symbol.toUpperCase());
      });
    });
    return symbols;
  }, [data]);

  const filteredTopMovers = useMemo(() => {
    if (showRealizedGains) return topMovers;
    return topMovers.filter((stock) => unrealizedTickerSymbols.has(stock.symbol.toUpperCase()));
  }, [showRealizedGains, topMovers, unrealizedTickerSymbols]);

  // Fetch BTCUSD market data to get updated_at timestamp
  const { data: btcusdData } = useQuery({
    queryKey: ['ticker-market', 'BTCUSD'],
    queryFn: async () => {
      if (!session?.access_token) {
        return null;
      }
      return getTickerMarket({
        ticker: 'BTCUSD',
        token: session.access_token,
      });
    },
    enabled: !!session?.access_token,
    staleTime: 5 * 60 * 1000, // 5 minutes (matches backend TTL)
  });

  // Format the last updated time in user's timezone
  const formattedLastUpdated = useMemo(() => {
    if (!btcusdData?.data) return null;
    
    // For single ticker response, data is TickerMarketData directly
    const tickerData = btcusdData.data as any;
    const updatedAt = tickerData.updated_at;
    
    if (!updatedAt) return null;
    
    try {
      const date = new Date(updatedAt);
      
      // Format date as "1/11/26"
      const month = date.getMonth() + 1; // getMonth() returns 0-11
      const day = date.getDate();
      const year = date.getFullYear().toString().slice(-2); // Last 2 digits of year
      const dateStr = `${month}/${day}/${year}`;
      
      // Format time as "4:50pm"
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'pm' : 'am';
      const hours12 = hours % 12 || 12; // Convert to 12-hour format
      const minutesStr = minutes.toString().padStart(2, '0');
      const timeStr = `${hours12}:${minutesStr}${ampm}`;
      
      // Get timezone abbreviation (e.g., "MDT", "EST")
      const timeZoneFormatter = new Intl.DateTimeFormat(undefined, {
        timeZoneName: 'short',
      });
      const parts = timeZoneFormatter.formatToParts(date);
      const timeZonePart = parts.find(part => part.type === 'timeZoneName');
      const timeZoneStr = timeZonePart?.value || '';
      
      return `${dateStr} ${timeStr} ${timeZoneStr}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return null;
    }
  }, [btcusdData]);

  return (
    <div className={`bg-white rounded-lg p-6 shadow-sm overflow-hidden ${className}`}>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-orange-600">
            Top Movers <span className="text-sm text-blue-500">(Today)</span>
          </h2>
          {formattedLastUpdated && (
            <div className="relative group">
              <img
                src="/icon-last-updated-lrg.png"
                alt="Last updated"
                className="h-4 w-4 cursor-pointer"
                aria-label="Last updated"
              />
              {/* Tooltip */}
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 hidden group-hover:block z-10">
                <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
                  <p className="text-left font-medium">Last ticker update:</p>
                  <p className="text-left whitespace-nowrap">{formattedLastUpdated}</p>
                  {/* Tooltip arrow pointing up */}
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                </div>
              </div>
            </div>
          )}
        </div>
        {!hideShowRealizedGains && <ShowRealizedGainsToggle />}
      </div>

      {(isLoading || isLoadingMarket) ? (
        <div className="text-center text-gray-500 text-sm">Loading movers...</div>
      ) : filteredTopMovers.length > 0 ? (
        <div className="flex overflow-x-auto space-x-4 pb-2">
          {filteredTopMovers.map((stock) => {
            const isActive = stock.symbol === selectedTicker;
            return (
              <button
                key={stock.symbol}
                onClick={() => onSelectTicker(stock.symbol)}
                aria-pressed={isActive}
                className={`
                  text-center flex-shrink-0 w-20 
                  py-2 px-3 rounded-lg 
                  transition-colors duration-150 
                  ${
                    isActive
                      ? 'bg-gray-200 text-gray-800'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }
                `}
              >
                <h3 className="font-semibold">{stock.symbol}</h3>
                <p className="text-xs truncate" title={stock.name}>
                  {stock.name}
                </p>
                <span
                  className={`font-semibold text-sm ${
                    stock.change.startsWith('+') ? 'text-green-500' : 'text-red-500'
                  }`}
                >
                  {stock.change}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-center text-gray-500 text-sm">No data available.</div>
      )}
    </div>
  );
};
